import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Plus, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import {
  useCreateInvoiceSet,
  useInvoiceSetPreview,
  useScheduleInvoiceSetInStripe,
} from './useInvoiceSetActions';
import type { InvoiceSetInstallment, ScheduleInvoiceSetReport } from './useInvoiceSetActions';

// Ruling 19: GO creates the local drafts and immediately sends them to Stripe.
// Ruling 27 adds the other ending — "Create as drafts to adjust first" stops
// after the write, so an admin can edit each draft's line items on its row in the
// Invoices card before a parent ever sees them. Either way a failed Stripe step
// keeps this dialog open on a result panel with "Retry send" rather than closing
// quietly.
//
// Ruling 28: there is no separate one-off invoice dialog. `mode="single"` is this
// same wizard with one installment and a due date, and the "Bill this monthly"
// toggle turns it back into a set without the admin starting over.

const STEPS = ['what', 'when', 'review'] as const;

export type InvoiceSetWizardMode = 'set' | 'single';

export interface InvoiceSetStudent {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

interface AdjustmentLine {
  key: string;
  description: string;
  amount: string;
}

interface InvoiceSetWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyEnrollmentId: string | null | undefined;
  students: readonly InvoiceSetStudent[];
  onCompleted: (notice?: string) => void;
  mode?: InvoiceSetWizardMode;
  // Why the family can't be charged automatically, when it can't; the server
  // refuses charge_automatically then, so the choice isn't offered.
  autoPayUnavailableReason?: string | null;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function formatCurrency(cents: number) {
  return currencyFormatter.format(cents / 100);
}

function formatDate(value: unknown) {
  if (typeof value !== 'string' || !value) return 'N/A';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function studentName(student: InvoiceSetStudent) {
  const name = [student.firstName, student.lastName].filter(Boolean).join(' ').trim();
  return name || 'Student';
}

function dollarsToCents(value: string) {
  const parsed = parseFloat(value || '0');
  if (!Number.isFinite(parsed)) return NaN;
  return Math.round(parsed * 100);
}

function defaultFirstDueOn() {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  date.setDate(1);
  return date.toISOString().slice(0, 10);
}

// A single invoice is due soon, not next month. Ruling 21 makes this the due
// date: the invoice finalizes in Stripe within minutes either way.
function defaultSingleDueOn() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

function evenSplit(totalCents: number, count: number) {
  if (count <= 0) return [];
  const base = Math.floor(totalCents / count);
  const parts = Array<number>(count).fill(base);
  parts[count - 1] += totalCents - base * count;
  return parts;
}

function newAdjustment(): AdjustmentLine {
  return { key: `extra-${Math.random().toString(36).slice(2, 9)}`, description: '', amount: '' };
}

function mutationError(
  payload: { userErrors?: readonly { message: string }[] | null } | null | undefined
) {
  return payload?.userErrors?.[0]?.message || null;
}

function lineAmountLabel(line: { amountCents: number; lineType?: string | null }) {
  const amount = formatCurrency(line.amountCents);
  return line.lineType === 'discount' ? `-${amount}` : amount;
}

function PreviewTable({ installments }: { installments: readonly InvoiceSetInstallment[] }) {
  if (installments.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Due</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Memo</TableHead>
            <TableHead>Lines</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {installments.map((installment) => (
            <TableRow key={`${installment.dueOn}-${installment.memo}`}>
              <TableCell>{formatDate(installment.dueOn)}</TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(installment.amountCents)}
              </TableCell>
              <TableCell>{installment.memo}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {installment.lines.map((line, index) => (
                  // Two extras can be identical in every field, so the position
                  // in the installment is the only thing that tells them apart.
                  <div key={`${index}-${line.description}-${line.amountCents}`}>
                    {line.description} · {lineAmountLabel(line)}
                  </div>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function InvoiceSetWizard({
  open,
  onOpenChange,
  familyEnrollmentId,
  students,
  onCompleted,
  mode = 'set',
  autoPayUnavailableReason = null,
}: InvoiceSetWizardProps) {
  const single = mode === 'single';
  const [currentStep, setCurrentStep] = useState(0);
  const [kind, setKind] = useState(single ? 'OTHER' : 'TUITION');
  const [description, setDescription] = useState(single ? '' : 'Tuition');
  const [totalAmount, setTotalAmount] = useState('0.00');
  const [splitPerStudent, setSplitPerStudent] = useState(!single && students.length > 0);
  const [studentAmounts, setStudentAmounts] = useState<Record<string, string>>({});
  // Finding 2: the total and the per-student boxes have to agree before the
  // wizard will move on, so until someone types in a box the boxes follow the
  // total. A hand-edited box turns that off — the admin has said what they want
  // — and "Split evenly" is how they hand the boxes back.
  const [autoSplit, setAutoSplit] = useState(true);
  const [discountLabel, setDiscountLabel] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [extraLines, setExtraLines] = useState<AdjustmentLine[]>([]);
  const [recurring, setRecurring] = useState(!single);
  const [installmentCount, setInstallmentCount] = useState(single ? '1' : '10');
  const [firstDueOn, setFirstDueOn] = useState(single ? defaultSingleDueOn() : defaultFirstDueOn());
  const [collectionMethod, setCollectionMethod] = useState('send_invoice');
  const [formError, setFormError] = useState<string | null>(null);
  const [scheduleKey, setScheduleKey] = useState<string | null>(null);
  const [report, setReport] = useState<ScheduleInvoiceSetReport | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [showResultPanel, setShowResultPanel] = useState(false);

  const [runPreview, { data: previewData, loading: previewLoading, error: previewError }] =
    useInvoiceSetPreview();
  const [createInvoiceSet, { loading: creating }] = useCreateInvoiceSet();
  const [scheduleInvoiceSetInStripe, { loading: scheduling }] = useScheduleInvoiceSetInStripe();

  const totalCents = dollarsToCents(totalAmount);
  const parsedInstallmentCount = recurring ? parseInt(installmentCount, 10) : 1;

  const perStudent = useMemo(() => {
    if (!splitPerStudent) return null;
    return students.map((student) => ({
      personId: student.id,
      amountCents: dollarsToCents(studentAmounts[student.id] ?? '0'),
    }));
  }, [splitPerStudent, students, studentAmounts]);

  const perStudentTotal = useMemo(
    () => (perStudent || []).reduce((sum, entry) => sum + (Number.isFinite(entry.amountCents) ? entry.amountCents : 0), 0),
    [perStudent]
  );

  // NaN is falsy, so treating "abc" as "no discount" would drop the admin's
  // input without a word. Blank means no discount; anything else must parse.
  const discountEntered = discountAmount.trim().length > 0;
  const discountCents = discountEntered ? dollarsToCents(discountAmount) : 0;
  const discount = useMemo(() => {
    if (!discountEntered || !Number.isFinite(discountCents) || discountCents <= 0) return null;
    return { description: discountLabel.trim(), amountCents: discountCents };
  }, [discountEntered, discountCents, discountLabel]);

  const filledExtraLines = useMemo(
    () => extraLines.filter((line) => line.description.trim() || line.amount.trim()),
    [extraLines]
  );

  const extraLinesInput = useMemo(() => {
    if (filledExtraLines.length === 0) return null;
    return filledExtraLines.map((line) => ({
      description: line.description.trim(),
      amountCents: dollarsToCents(line.amount),
    }));
  }, [filledExtraLines]);

  const whatError = useMemo(() => {
    if (!description.trim()) return 'Description is required.';
    if (!Number.isFinite(totalCents) || totalCents <= 0) return 'Total must be greater than $0.';
    if (splitPerStudent) {
      if (students.length === 0) return 'This enrollment has no students to split across.';
      if ((perStudent || []).some((entry) => !Number.isFinite(entry.amountCents) || entry.amountCents < 0)) {
        return 'Each student amount must be $0 or more.';
      }
      if (perStudentTotal !== totalCents) {
        return `The per-student amounts add up to ${formatCurrency(perStudentTotal)}, not ${formatCurrency(totalCents)}.`;
      }
    }
    if (discountEntered) {
      if (!Number.isFinite(discountCents) || discountCents <= 0) return 'The discount must be an amount greater than $0.';
      if (!discountLabel.trim()) return 'The discount needs a label.';
      if (discountCents >= totalCents) return 'The discount cannot be as large as the total.';
    }
    if ((extraLinesInput || []).some((line) => !line.description)) return 'Each extra line needs a label.';
    if ((extraLinesInput || []).some((line) => !Number.isFinite(line.amountCents) || line.amountCents <= 0)) {
      return 'Each extra line must be greater than $0.';
    }
    return null;
  }, [
    description,
    totalCents,
    splitPerStudent,
    students.length,
    perStudent,
    perStudentTotal,
    discountEntered,
    discountCents,
    discountLabel,
    extraLinesInput,
  ]);

  const whenError = useMemo(() => {
    if (!Number.isFinite(parsedInstallmentCount) || parsedInstallmentCount < 1 || parsedInstallmentCount > 24) {
      return 'Installments must be between 1 and 24.';
    }
    if (!firstDueOn) return 'A due date is required.';
    return null;
  }, [parsedInstallmentCount, firstDueOn]);

  // The builder takes one signed list of line items, each carrying its WHOLE
  // amount across the installments. An extra line is charged on every
  // installment, so its whole amount is the per-installment amount times the
  // count, which splits back to exactly that amount each time; the discount is
  // a negative line.
  const lineItems = useMemo(() => {
    const items = (extraLinesInput || []).map((line) => ({
      description: line.description,
      amountCents: line.amountCents * parsedInstallmentCount,
    }));
    if (discount) items.push({ description: discount.description, amountCents: -discount.amountCents });
    return items.length > 0 ? items : null;
  }, [extraLinesInput, discount, parsedInstallmentCount]);

  const setInput = useMemo(() => {
    if (!familyEnrollmentId || whatError || whenError) return null;
    return {
      familyEnrollmentId,
      kind,
      description: description.trim(),
      totalCents,
      installmentCount: parsedInstallmentCount,
      firstDueOn,
      perStudent,
      lineItems,
    };
  }, [
    familyEnrollmentId,
    whatError,
    whenError,
    kind,
    description,
    totalCents,
    parsedInstallmentCount,
    firstDueOn,
    perStudent,
    lineItems,
  ]);

  // The collection method is part of the preview: charged automatically to a
  // card, each invoice carries a card fee line.
  const previewKey = setInput ? JSON.stringify({ input: setInput, collectionMethod }) : null;

  // Debounced: the admin types an installment count and a date, and every
  // keystroke would otherwise be its own preview round trip.
  useEffect(() => {
    if (!open || currentStep === 0 || showResultPanel || !previewKey) return undefined;
    const timer = window.setTimeout(() => {
      void runPreview({ variables: JSON.parse(previewKey) });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, currentStep, showResultPanel, previewKey, runPreview]);

  const installments = (previewData?.invoiceSetPreview ?? []) as readonly InvoiceSetInstallment[];

  const resetState = () => {
    setCurrentStep(0);
    setKind(single ? 'OTHER' : 'TUITION');
    setDescription(single ? '' : 'Tuition');
    setTotalAmount('0.00');
    setSplitPerStudent(!single && students.length > 0);
    setStudentAmounts({});
    setAutoSplit(true);
    setDiscountLabel('');
    setDiscountAmount('');
    setExtraLines([]);
    setRecurring(!single);
    setInstallmentCount(single ? '1' : '10');
    setFirstDueOn(single ? defaultSingleDueOn() : defaultFirstDueOn());
    setCollectionMethod('send_invoice');
    setFormError(null);
    setScheduleKey(null);
    setReport(null);
    setScheduleError(null);
    setShowResultPanel(false);
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  // Closing the result panel leaves the drafts in place; selecting and posting
  // them from the Invoices card is the later retry.
  const handleCloseAfterFailure = () => {
    resetState();
    onOpenChange(false);
    onCompleted();
  };

  const applyEvenSplit = (cents: number) => {
    if (!Number.isFinite(cents) || cents <= 0 || students.length === 0) return;
    const parts = evenSplit(cents, students.length);
    const next: Record<string, string> = {};
    students.forEach((student, index) => {
      next[student.id] = (parts[index] / 100).toFixed(2);
    });
    setStudentAmounts(next);
  };

  const handleSplitEvenly = () => {
    setAutoSplit(true);
    applyEvenSplit(totalCents);
  };

  // Turning the split back on must not leave empty boxes that then fail the
  // "amounts add up to the total" check. Boxes nobody has typed in are filled
  // from the total; hand-edited ones are left as the admin left them.
  const handleSplitToggle = (next: boolean) => {
    setSplitPerStudent(next);
    if (!next) return;

    const untouched = students.every((student) => !(studentAmounts[student.id] ?? '').trim());
    if (!autoSplit && !untouched) return;

    setAutoSplit(true);
    applyEvenSplit(totalCents);
  };

  const handleTotalChange = (value: string) => {
    setTotalAmount(value);
    if (splitPerStudent && autoSplit) applyEvenSplit(dollarsToCents(value));
  };

  const handleNext = () => {
    const stepError = currentStep === 0 ? whatError : whenError;
    if (stepError) {
      setFormError(stepError);
      return;
    }
    setFormError(null);
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setFormError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  // The mutation defines `success` as "no failed rows". `pushedCount` counts only
  // what this run put in Stripe, so on a retry the rows an earlier run already
  // pushed come back as `already_pushed` and are not counted — comparing it to
  // the set size would read every successful retry as a failure.
  const sendToStripe = async (key: string) => {
    setScheduleError(null);

    const result = await scheduleInvoiceSetInStripe({
      variables: { input: { familyEnrollmentId: familyEnrollmentId!, scheduleKey: key, collectionMethod } },
    });

    const payload = result.data?.scheduleInvoiceSetInStripe;
    const message = mutationError(payload);
    setReport((payload?.report ?? null) as ScheduleInvoiceSetReport | null);

    if (message || !payload?.success) {
      setScheduleError(message || 'Stripe did not accept the whole invoice set.');
      setShowResultPanel(true);
      return false;
    }

    return true;
  };

  // Both endings start here: the drafts are written first either way, so a set
  // that fails to reach Stripe is never lost.
  const createDrafts = async () => {
    setFormError(null);

    if (!familyEnrollmentId) {
      setFormError('Family enrollment is required.');
      return null;
    }
    if (!setInput) {
      setFormError(whatError || whenError || 'The invoice set is incomplete.');
      return null;
    }

    const created = await createInvoiceSet({ variables: { input: setInput } });
    const createPayload = created.data?.createInvoiceSet;
    const createMessage = mutationError(createPayload);
    if (createMessage || !createPayload?.scheduleKey) {
      setFormError(createMessage || 'The invoice set could not be created.');
      return null;
    }

    setScheduleKey(createPayload.scheduleKey);
    return { key: createPayload.scheduleKey, count: createPayload.invoices?.length ?? parsedInstallmentCount };
  };

  const handleGo = async () => {
    const created = await createDrafts();
    if (!created) return;

    const sent = await sendToStripe(created.key);
    if (!sent) return;

    resetState();
    onOpenChange(false);
    onCompleted();
  };

  // Ruling 27: stop after the write. Nothing is in Stripe, no parent sees a
  // draft, and the Invoices card is where the adjusting and the posting happen —
  // the line items on each draft's own row, the post from the selected rows.
  const handleCreateDraftsOnly = async () => {
    const created = await createDrafts();
    if (!created) return;

    const noun = created.count === 1 ? 'draft' : 'drafts';
    resetState();
    onOpenChange(false);
    // The collection method is not stored on a draft — nothing has reached
    // Stripe — so the post asks for it again. Say so rather than let the
    // choice made here look like it was kept.
    onCompleted(
      `${created.count} ${noun} created — adjust them, then select them on the Invoices card ` +
        'and post them, where you choose how they are collected.'
    );
  };

  const handleRetrySend = async () => {
    if (!scheduleKey) return;
    const sent = await sendToStripe(scheduleKey);
    if (!sent) return;

    resetState();
    onOpenChange(false);
    onCompleted();
  };

  const previewStatus = (
    <>
      {previewLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Building preview...
        </div>
      )}
      {previewError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Could not build the preview: {previewError.message}
        </div>
      )}
    </>
  );

  const previewedCount = installments.length || parsedInstallmentCount;
  const invoiceCountLabel = previewedCount === 1 ? 'This invoice' : `These ${previewedCount} invoices`;
  const reviewTitle = recurring ? 'Review the invoices' : 'Review the invoice';
  const stepTitle = showResultPanel
    ? 'Stripe did not take every invoice'
    : currentStep === 0
      ? 'What are you billing?'
      : currentStep === 1
        ? 'When is it due?'
        : reviewTitle;

  const busy = creating || scheduling;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        // Escape or an outside click must not abandon a set mid-write: the
        // drafts would exist with nothing on screen naming them.
        if (busy) return;
        if (showResultPanel) {
          handleCloseAfterFailure();
          return;
        }
        handleClose();
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{stepTitle}</DialogTitle>
          <DialogDescription>
            {showResultPanel
              ? 'The drafts were created. Retry the send, or close and post them from the Invoices card later.'
              : `Step ${currentStep + 1} of ${STEPS.length}`}
          </DialogDescription>
        </DialogHeader>

        {showResultPanel ? (
          <div className="space-y-4">
            {scheduleError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {scheduleError}
              </div>
            )}

            {(report?.warnings ?? []).length > 0 && (
              <div className="space-y-1 text-sm text-amber-700">
                {report!.warnings.map((warning) => (
                  <div key={warning} className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            )}

            {(report?.rows ?? []).length > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Detail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report!.rows.map((row) => (
                      <TableRow key={row.invoiceId}>
                        <TableCell className="font-mono text-xs">{row.invoiceId}</TableCell>
                        <TableCell>{row.action}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {row.message || ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {currentStep === 0 && (
              <>
                <div className="space-y-2">
                  <Label>Kind</Label>
                  <RadioGroup
                    value={kind}
                    onValueChange={(value) => {
                      setKind(value);
                      setDescription((current) =>
                        current === 'Tuition' && value === 'OTHER' ? '' : current || 'Tuition'
                      );
                      // Only tuition is charged automatically; anything else is sent to pay.
                      if (value !== 'TUITION') setCollectionMethod('send_invoice');
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="TUITION" id="invoice-set-kind-tuition" />
                      <Label htmlFor="invoice-set-kind-tuition" className="font-normal">Tuition</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="OTHER" id="invoice-set-kind-other" />
                      <Label htmlFor="invoice-set-kind-other" className="font-normal">Other</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invoice-set-description">Description</Label>
                  <Input
                    id="invoice-set-description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="Tuition"
                  />
                  <p className="text-xs text-muted-foreground">
                    Appears in every memo and line item, e.g. &quot;Tuition&quot; or &quot;Sports fee&quot;.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invoice-set-total">
                    {recurring ? 'Total for the whole set' : 'Amount'}
                  </Label>
                  <Input
                    id="invoice-set-total"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={totalAmount}
                    onChange={(event) => handleTotalChange(event.target.value)}
                  />
                </div>

                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        id="invoice-set-no-split"
                        type="checkbox"
                        checked={!splitPerStudent}
                        onChange={(event) => handleSplitToggle(!event.target.checked)}
                      />
                      <Label htmlFor="invoice-set-no-split" className="font-normal">
                        One line, no per-student split
                      </Label>
                    </div>
                    {splitPerStudent && (
                      <Button type="button" variant="outline" size="sm" onClick={handleSplitEvenly}>
                        Split evenly
                      </Button>
                    )}
                  </div>

                  {splitPerStudent && students.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      This enrollment has no students, so the set must be one line.
                    </p>
                  )}

                  {splitPerStudent && students.length > 0 && (
                    <div className="space-y-2">
                      {students.map((student) => (
                        <div key={student.id} className="flex items-center justify-between gap-3">
                          <Label htmlFor={`invoice-set-student-${student.id}`} className="font-normal">
                            {studentName(student)}
                          </Label>
                          <Input
                            id={`invoice-set-student-${student.id}`}
                            className="w-40"
                            type="number"
                            step="0.01"
                            min="0"
                            value={studentAmounts[student.id] ?? ''}
                            onChange={(event) => {
                              setAutoSplit(false);
                              setStudentAmounts((current) => ({
                                ...current,
                                [student.id]: event.target.value,
                              }));
                            }}
                          />
                        </div>
                      ))}
                      <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                        <span>Per-student total</span>
                        <span>{formatCurrency(perStudentTotal)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Ruling 27: a discount comes off the whole set and is split across
                    the installments; an extra line is charged on every one. */}
                <div className="space-y-3 border-t pt-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="invoice-set-discount-label">Discount label</Label>
                      <Input
                        id="invoice-set-discount-label"
                        value={discountLabel}
                        onChange={(event) => setDiscountLabel(event.target.value)}
                        placeholder="Sibling discount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invoice-set-discount-amount">
                        {recurring ? 'Discount for the whole set' : 'Discount'}
                      </Label>
                      <Input
                        id="invoice-set-discount-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={discountAmount}
                        onChange={(event) => setDiscountAmount(event.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label>Extra lines</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setExtraLines((current) => [...current, newAdjustment()])}
                      >
                        <Plus className="h-4 w-4" />
                        Add line
                      </Button>
                    </div>
                    {extraLines.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        {recurring
                          ? 'A fee added to every installment, e.g. a monthly technology fee.'
                          : 'Another charge on this invoice, e.g. a lab fee.'}
                      </p>
                    )}
                    {extraLines.map((line, index) => (
                      <div key={line.key} className="flex items-center gap-2">
                        <Input
                          aria-label={`Extra line ${index + 1} label`}
                          value={line.description}
                          onChange={(event) =>
                            setExtraLines((current) =>
                              current.map((entry) =>
                                entry.key === line.key ? { ...entry, description: event.target.value } : entry
                              )
                            )
                          }
                          placeholder="Technology fee"
                        />
                        <Input
                          aria-label={`Extra line ${index + 1} amount`}
                          className="w-32"
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.amount}
                          onChange={(event) =>
                            setExtraLines((current) =>
                              current.map((entry) =>
                                entry.key === line.key ? { ...entry, amount: event.target.value } : entry
                              )
                            )
                          }
                          placeholder="0.00"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove extra line ${index + 1}`}
                          onClick={() =>
                            setExtraLines((current) => current.filter((entry) => entry.key !== line.key))
                          }
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {currentStep === 1 && (
              <>
                <div className="flex items-center gap-2">
                  <input
                    id="invoice-set-recurring"
                    type="checkbox"
                    checked={recurring}
                    onChange={(event) => {
                      setRecurring(event.target.checked);
                      if (!event.target.checked) setInstallmentCount('1');
                      else if (installmentCount === '1') setInstallmentCount('10');
                    }}
                  />
                  <Label htmlFor="invoice-set-recurring" className="font-normal">
                    Bill this monthly
                  </Label>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {recurring && (
                    <div className="space-y-2">
                      <Label htmlFor="invoice-set-installments">Installments</Label>
                      <Input
                        id="invoice-set-installments"
                        type="number"
                        min="1"
                        max="24"
                        value={installmentCount}
                        onChange={(event) => setInstallmentCount(event.target.value)}
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="invoice-set-first-due">
                      {recurring ? 'First due date' : 'Due date'}
                    </Label>
                    <Input
                      id="invoice-set-first-due"
                      type="date"
                      value={firstDueOn}
                      onChange={(event) => setFirstDueOn(event.target.value)}
                    />
                  </div>
                </div>

                {/* Decision 3: manual unless the admin deliberately picks the saved
                    payment method, and the server refuses that without consent.
                    Automatic collection is tuition only, so for anything else the
                    choice is not on offer. */}
                <div className="space-y-2">
                  <Label htmlFor="invoice-set-collection">Collection</Label>
                  <Select value={collectionMethod} onValueChange={setCollectionMethod}>
                    <SelectTrigger id="invoice-set-collection">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="send_invoice">Manual payment in SolaStack</SelectItem>
                      <SelectItem
                        value="charge_automatically"
                        disabled={kind !== 'TUITION' || Boolean(autoPayUnavailableReason)}
                      >
                        Charge saved payment method
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {kind !== 'TUITION' ? (
                    <p className="text-sm text-muted-foreground">
                      Only tuition can be charged automatically; other invoices are sent for the family to pay.
                    </p>
                  ) : autoPayUnavailableReason && (
                    <p className="text-sm text-muted-foreground">{autoPayUnavailableReason}</p>
                  )}
                  {collectionMethod === 'send_invoice' && (
                    <p className="text-sm text-muted-foreground">
                      If invoice emails are enabled for this school, posting queues notices to guardian
                      email addresses. Check each invoice's notice status in Billing after posting.
                    </p>
                  )}
                </div>

                {previewStatus}
                <PreviewTable installments={installments} />
              </>
            )}

            {currentStep === 2 && (
              <>
                {previewStatus}
                <PreviewTable installments={installments} />
                <p className="text-sm text-muted-foreground">
                  {collectionMethod === 'charge_automatically'
                    ? `${invoiceCountLabel} will charge the family's saved payment method on ${recurring ? 'their due dates' : 'its due date'}.`
                    : `${invoiceCountLabel} will be available in My Invoices for the family to pay from
                      their SolaStack account. Invoice notices are queued when enabled for this school;
                      check their status in Billing after posting. Automatic reminders are not sent.`}
                </p>
                <p className="text-sm text-muted-foreground">
                  Or create as drafts: nothing reaches Stripe and no parent sees anything until you select
                  and post them from the Invoices card, where you pick the collection method again.
                </p>
              </>
            )}

            {formError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {formError}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between">
          {showResultPanel ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCloseAfterFailure} disabled={busy}>
                Close
              </Button>
              <Button
                onClick={(event) => {
                  event.preventDefault();
                  void handleRetrySend();
                }}
                disabled={busy}
              >
                {scheduling ? 'Sending...' : 'Retry send'}
              </Button>
            </div>
          ) : (
            <>
              <div>
                {currentStep > 0 && (
                  <Button variant="outline" onClick={handleBack} disabled={busy}>
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleClose} disabled={busy}>
                  Cancel
                </Button>
                {currentStep < STEPS.length - 1 ? (
                  <Button onClick={handleNext} disabled={busy}>
                    Next
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={(event) => {
                        event.preventDefault();
                        void handleCreateDraftsOnly();
                      }}
                      disabled={busy}
                    >
                      {creating && !scheduling ? 'Creating...' : 'Create as drafts to adjust first'}
                    </Button>
                    <Button
                      onClick={(event) => {
                        event.preventDefault();
                        void handleGo();
                      }}
                      disabled={busy}
                    >
                      {busy ? 'Sending...' : 'Create and send to Stripe'}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceSetWizard;
